import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Collection name for waitlist entries
const WAITLIST_COLLECTION = 'waitlist';

/**
 * Add a new email to the waitlist
 * @param {string} email - Email address to add
 * @returns {Promise<boolean>} - Success status
 */
export const addToWaitlist = async (email) => {
  try {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }

    // Check if email already exists
    const existingEntries = await getWaitlistEntries();
    const emailExists = existingEntries.some(entry => entry.email.toLowerCase() === email.toLowerCase());
    
    if (emailExists) {
      throw new Error('Email already exists in waitlist');
    }

    // Add new entry
    const docRef = await addDoc(collection(db, WAITLIST_COLLECTION), {
      email: email.toLowerCase().trim(),
      timestamp: serverTimestamp(),
      status: 'pending'
    });

    console.log('Waitlist entry added with ID: ', docRef.id);
    return true;
  } catch (error) {
    console.error('Error adding to waitlist: ', error);
    
    // Handle specific Firebase errors
    if (error.code === 'permission-denied') {
      throw new Error('Database access denied. Please contact support.');
    } else if (error.code === 'unavailable') {
      throw new Error('Service temporarily unavailable. Please try again later.');
    } else if (error.message.includes('Firebase')) {
      throw new Error('Connection error. Please check your internet connection.');
    }
    
    throw error;
  }
};

/**
 * Get all waitlist entries (admin only)
 * @returns {Promise<Array>} - Array of waitlist entries
 */
export const getWaitlistEntries = async () => {
  try {
    const q = query(collection(db, WAITLIST_COLLECTION), orderBy('timestamp', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const entries = [];
    querySnapshot.forEach((doc) => {
      entries.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return entries;
  } catch (error) {
    console.error('Error getting waitlist entries: ', error);
    
    // Handle specific Firebase errors
    if (error.code === 'permission-denied') {
      console.error('Firestore permission denied. Make sure Firestore is enabled.');
      return [];
    } else if (error.code === 'unavailable') {
      console.error('Firestore service unavailable.');
      return [];
    }
    
    throw error;
  }
};

/**
 * Get waitlist count
 * @returns {Promise<number>} - Total number of waitlist entries
 */
export const getWaitlistCount = async () => {
  try {
    const entries = await getWaitlistEntries();
    return entries.length;
  } catch (error) {
    console.error('Error getting waitlist count: ', error);
    return 0;
  }
};
